import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import axios from "../lib/axios";
import { subscribeRealtime } from "../lib/realtimeEvents";
import { useAuth } from "./AuthContext";

const ISIGHT_BASE_URL =
  import.meta.env.VITE_ISIGHT_URL || "https://isight.netsights.ai";
const MASTER_SYNC_BAR_DURATION_MS = 60_000;

export interface ConnectionStatus {
  meta: boolean;
  googleAds: boolean;
  ga4: boolean;
  gsc: boolean;
  clarity?: boolean;
}

export type MasterSyncStep = string;

export type ConnectionName = "shopify" | "meta" | "ga4" | "googleAds" | "gsc" | "clarity" | string;

type TriggerMasterSyncOptions = {
  connectionName?: ConnectionName;
  /** If provided, ensures this step is included (even if not connected yet). */
  step?: MasterSyncStep;
};

interface MasterSyncContextType {
  connectionStatus: ConnectionStatus | null;
  hasBlendedData: boolean;
  isSyncing: boolean;
  fetchConnectionStatus: (shop: string) => Promise<ConnectionStatus>;
  checkBlendedData: (shop: string) => Promise<boolean>;
  triggerMasterSync: (shop: string, opts?: TriggerMasterSyncOptions) => Promise<void>;
  backfillKpis: (shop: string, opts: { only: string[]; days?: number }) => Promise<void>;
  backfillKpisForConnection: (shop: string, connectionName: ConnectionName) => Promise<void>;
  refreshBlendedStatus: (shop: string) => Promise<void>;
  // Immediately hides the "Importing data..." top bar — call this right after
  // a successful disconnect so the bar doesn't keep running its fixed timer.
  stopMasterSyncBar: () => void;
}
const MasterSyncContext = createContext<MasterSyncContextType | undefined>(
  undefined
);

export function useMasterSync() {
  const ctx = useContext(MasterSyncContext);
  if (ctx === undefined) {
    throw new Error("useMasterSync must be used within MasterSyncProvider");
  }
  return ctx;
}

function resolveStepsForConnection(connectionName: string, step?: string): string[] {
  const name = connectionName.toLowerCase();
  let resolved: string[] = [];

  if (name === "shopify") {
    resolved = ["ceo-report"];
  } else if (name === "meta" || name === "googleads" || name === "google_ads" || name === "ads" || name === "facebook" ) {
    resolved = ["ads", "blended", "ceo-report"];
  } else {
    resolved = [name, "blended", "ceo-report"];
  }

  if (step && !resolved.includes(step)) {
    resolved.push(step);
  }

  return resolved;
}

function resolveStepsForStep(step: string): string[] {
  const stepLower = step.toLowerCase();
  if (stepLower === "ceo-report") {
    return ["ceo-report"];
  }
  if (stepLower === "ads") {
    return ["ads", "blended", "ceo-report"];
  }
  return [stepLower, "blended", "ceo-report"];
}

function buildSteps(connections: ConnectionStatus): MasterSyncStep[] {
  const steps: MasterSyncStep[] = ["ceo-report"]; // Shopify is always active
  if (connections.meta || connections.googleAds) {
    steps.push("ads");
  }
  if (connections.ga4) {
    steps.push("ga4");
  }
  if (connections.gsc) {
    steps.push("gsc");
  }
  if (connections.clarity) {
    steps.push("clarity");
  }
  if (
    connections.meta ||
    connections.googleAds ||
    connections.ga4 ||
    connections.gsc ||
    connections.clarity
  ) {
    steps.push("blended");
  }
  return steps;
}

export function MasterSyncProvider({ children }: { children: ReactNode }) {
  const { currentShop, stores} = useAuth();
  const [connectionStatus, setConnectionStatus] =
  useState<ConnectionStatus | null>(null);
  const [hasBlendedData, setHasBlendedData] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const barTimeoutRef             = useRef<ReturnType<typeof setTimeout> | null>(null);
  const token                     = "8f3d9b422e5a10c71f92e48bbd6d5a123f19c9640982741d5b2c8a3e9f012345";
  const fetchConnectionStatus     = useCallback(
    async (shop: string): Promise<ConnectionStatus> => {
      // const shopifyConnected = stores.some(
      //   (s) => s.shop === shop && s.connected === true
      // );

      const [metaRes, googleAdsRes, ga4Res, gscRes] = await Promise.allSettled([
        axios.get("/facebook/account", shop ? { params: { shop } } : undefined),
        axios.get("/googleAd/account", shop ? { params: { shop } } : undefined),
        axios.get("/ga4/account", shop ? { params: { shop } } : undefined),
        axios.get("/gsc/account", shop ? { params: { shop } } : undefined),
      ]);

      const meta =
        metaRes.status === "fulfilled" &&
        metaRes.value?.data?.account != null;
      const googleAds =
        googleAdsRes.status === "fulfilled" &&
        Array.isArray(googleAdsRes.value?.data?.accounts) &&
        googleAdsRes.value.data.accounts.length > 0;
      const ga4 =
        ga4Res.status === "fulfilled" && ga4Res.value?.data?.account != null;
      const gsc =
        gscRes.status === "fulfilled" &&
        gscRes.value?.data?.account != null;

      const status: ConnectionStatus = {
        meta,
        googleAds,
        ga4,
        gsc,
      };
      setConnectionStatus(status);
      return status;
    },
    [stores]
  );

  const checkBlendedData = useCallback(async (shop: string): Promise<boolean> => {
    try {
      const res = await axios.get("/kpi/marketing/blended-cac", {
        params: { shop },
      });
      const data = res?.data;
      if (!data) {
        setHasBlendedData(false);
        return false;
      }
      const items = Array.isArray(data) ? data : data?.data ?? data?.items ?? [];
      const hasData =
        Array.isArray(items) &&
        items.length > 0 &&
        items.some(
          (x: any) =>
            x?.blended_cac != null ||
            x?.current?.blended_cac != null
        );
      setHasBlendedData(!!hasData);
      return !!hasData;
    } catch {
      setHasBlendedData(false);
      return false;
    }
  }, []);

  const refreshBlendedStatus = useCallback(
    async (shop: string) => {
      await checkBlendedData(shop);
    },
    [checkBlendedData]
  );

  const backfillKpis = useCallback(
    async (shop: string, opts: { only: string[]; days?: number }) => {
      if (!shop) return;
      const only = Array.isArray(opts.only) ? opts.only : [];
      if (only.length === 0) return;
      if (!token) return;

      try {
        await fetch(`${ISIGHT_BASE_URL}/api/netsight/backfill-kpis`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            shop,
            days: 365,
            only,
          }),
          credentials: "include",
        });
      } catch (err) {
        console.error("Backfill KPIs failed:", err);
      }
    },
    [token]
  );

  const backfillKpisForConnection = useCallback(
    async (shop: string, connectionName: ConnectionName) => {
      const onlyByConnection: Record<ConnectionName, string[]> = {
        meta: [
          "meta_ads_revenue",
          "meta_ads_cac",
          "meta_ads_roas",
          "meta_ads_mer",
          "meta_ads_new_customers",
          "meta_ads_cpp",
          "meta_ads_frequency",
          "meta_ads_ctr",
          "meta_ads_scroll_stop_rate",
          "meta_ads_profit",
          "meta_ads_spend",
          "meta_ads_impressions",
          "meta_ads_clicks",
        ],
        ga4: [
          "ga4_sessions",
          "ga4_users",
          "ga4_new_users",
          "ga4_revenue",
          "ga4_conversions",
          "ga4_conversion_rate",
          "ga4_bounce_rate",
          "ga4_avg_session_duration",
          "ga4_pages_per_session",
          "ga4_organic_traffic",
          "ga4_most_viewed_pages",
        ],
        google_ads: [
          "google_ads_revenue",
          "google_ads_cac",
          "google_ads_roas",
          "google_ads_mer",
          "google_ads_new_customers",
          "google_ads_cpp",
          "google_ads_profit",
          "google_ads_spend",
          "google_ads_impressions",
          "google_ads_clicks",
        ],
      };

      await backfillKpis(shop, { only: onlyByConnection[connectionName], days: 7 });
    },
    [backfillKpis]
  );

  const stopMasterSyncBar = useCallback(() => {
    if (barTimeoutRef.current) {
      clearTimeout(barTimeoutRef.current);
      barTimeoutRef.current = null;
    }
    setIsSyncing(false);
  }, []);

  const triggerMasterSync = useCallback(
    async (shop: string, opts?: TriggerMasterSyncOptions) => {
      if (!shop) return;

      let steps: MasterSyncStep[];
      if (opts?.connectionName) {
        // Caller tells us which connection changed - resolve dynamically
        steps = resolveStepsForConnection(opts.connectionName, opts.step);
      } else if (opts?.step) {
        // Specific step requested - resolve dynamically
        steps = resolveStepsForStep(opts.step);
      } else {
        // Full sync - fetch connections to determine which steps to run
        const connections = await fetchConnectionStatus(shop);
        steps = buildSteps(connections);
      }
      if (steps.length === 0) return;
      if (!token) return;

      if (barTimeoutRef.current) {
        clearTimeout(barTimeoutRef.current);
        barTimeoutRef.current = null;
      }
      setIsSyncing(true);

      try {
        await fetch(`${ISIGHT_BASE_URL}/api/netsight/master-sync`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            shop,
            steps,
            connection_name: opts?.connectionName,
            step: opts?.step,
          }),
          credentials: "include",
        });
      } catch (err) {
        console.error("Master sync failed:", err);
      } finally {
        barTimeoutRef.current = setTimeout(() => {
          setIsSyncing(false);
          barTimeoutRef.current = null;
        }, MASTER_SYNC_BAR_DURATION_MS);
      }
    },
    [fetchConnectionStatus, token]
  );

  // When currentShop is set, fetch connection status and blended data
  useEffect(() => {
    if (!currentShop) return;
    fetchConnectionStatus(currentShop).catch(() => {});
    checkBlendedData(currentShop).catch(() => {});
  }, [currentShop, fetchConnectionStatus, checkBlendedData]);

  // Trigger master-sync only when a store/integration changes.
  useEffect(() => {
    const handleStoreConnected = (
      e: CustomEvent<{ shop: string; connectionName?: ConnectionName; step?: MasterSyncStep }>
    ) => {
      const shop = e.detail?.shop;
      if (shop) {
        triggerMasterSync(shop, {
          connectionName: e.detail?.connectionName,
          step: e.detail?.step,
        });
      }
    };

    window.addEventListener("storeConnected" as any, handleStoreConnected as any);

    const unsubscribeRealtime = subscribeRealtime<{
      shop?: string;
      connectionName?: ConnectionName;
      connection_name?: ConnectionName;
      step?: MasterSyncStep;
    }>(
      {
        "account.updated": (payload) => {
          const shop = payload.shop || currentShop;
          if (!shop) return;
          void fetchConnectionStatus(shop);
          void triggerMasterSync(shop, {
            connectionName: payload.connectionName || payload.connection_name,
            step: payload.step,
          });
        },
        "integration.account.updated": (payload) => {
          const shop = payload.shop || currentShop;
          if (!shop) return;
          void fetchConnectionStatus(shop);
          void triggerMasterSync(shop, {
            connectionName: payload.connectionName || payload.connection_name,
            step: payload.step,
          });
        },
        "connection.updated": (payload) => {
          const shop = payload.shop || currentShop;
          if (!shop) return;
          void fetchConnectionStatus(shop);
          void triggerMasterSync(shop, {
            connectionName: payload.connectionName || payload.connection_name,
            step: payload.step,
          });
        },
      },
      { shop: currentShop },
    );

    return () => {
      unsubscribeRealtime();
      window.removeEventListener("storeConnected" as any, handleStoreConnected as any);
    };
  }, [currentShop, fetchConnectionStatus, triggerMasterSync]);

  useEffect(() => {
    return () => {
      if (barTimeoutRef.current) clearTimeout(barTimeoutRef.current);
    };
  }, []);

  const value: MasterSyncContextType = {
    connectionStatus,
    hasBlendedData,
    isSyncing,
    fetchConnectionStatus,
    checkBlendedData,
    triggerMasterSync,
    backfillKpis,
    backfillKpisForConnection,
    refreshBlendedStatus,
    stopMasterSyncBar,
  };

  return (
    <MasterSyncContext.Provider value={value}>
      {children}
    </MasterSyncContext.Provider>
  );
}
